import { defineStore } from "pinia";
import { systemApi, translationApi } from "@/api";
import { GAME_ID } from "./index";
import { getLanguageFromStorage, saveLanguageToStorage } from "@/utils";
import { setI18nLocale, setLanguageStore } from "@/i18n";
import i18n from "@/i18n";
import { getGameConfig } from "@/config/gameConfig";

export const useLanguageStore = defineStore("language", {
  state: () => ({
    // 语种列表
    languageList: [] as any[],
    // API 返回的默认语言，固定
    defaultLanguage: "en-US",
    // 当前选择的语言，可能是 "all" 或具体语言代码
    curLanguage: "en-US",
    // 后台语言包
    languagePack: {},
    // 后台图片素材包
    imagePack: {},
    // 存储翻译后的内容，key 为 reply.id，value 为翻译后的内容
    translatedContents: {} as Record<number, string>,
    // 控制积分/商店入口显示，默认 false
    showPoint: false,
    // 控制分享到游戏内按钮显示，默认 false
    showShare: false,
    // 控制搜索入口显示，默认 false
    showSearch: false,
    // 是否有游戏配置（ext_params 有效时为 true，为 null 时 false，用于路由跳转维护页）
    hasGameConfig: false,
    // 扩展参数中的游戏真实名称（getExtParams 返回的 real_name），用于头部展示
    realName: "",
  }),

  getters: {
    /**
     * 获取当前选择的语言名称
     * 如果当前选择的是"全部"，返回默认语言的名称，否则返回当前语言的名称
     * @returns 语言名称
     */
    currentLanguageName(): string {
      // 如果选择的是"全部"，返回默认语言的名称
      const languageCode =
        this.curLanguage === "all" ? this.defaultLanguage : this.curLanguage;
      if (!languageCode) return "";
      const lang = this.languageList.find((l: any) => l.code === languageCode);
      return lang?.name || languageCode;
    },
    /**
     * 获取目标语言
     * 如果语言列表长度为1，返回"all"，否则返回当前选择的语言
     * @returns 目标语言代码
     */
    targetLanguage(): string {
      if (this.languageList.length === 1) {
        return "all";
      }
      return this.curLanguage;
    },
  },

  actions: {
    /**
     * 获取语种列表
     */
    async getLanguageList(gameId: string) {
      const response = await systemApi.getLanguage(gameId);
      if (response.code == 200) {
        const languageList = response.data?.language_list || [];
        this.defaultLanguage = response.data?.default || "all";
        // 如果语言列表长度大于1，在第一项添加"语言"选项
        if (languageList.length > 1) {
          const languageText = i18n.global.t("common.language");
          this.languageList = [
            { name: languageText, code: "all" },
            ...languageList,
          ];
        } else {
          this.languageList = languageList;
        }
        return response;
      }
    },
    /**
     * 获取扩展参数
     * @param gameId - 游戏ID
     * @returns Promise<ApiResponse>
     */
    async getExtParams(gameId: string) {
      const response = await systemApi.getExtParams(gameId);
      if (response.code == 200) {
        const extParams = response.data.ext_params;
        if (!extParams) {
          this.hasGameConfig = false;
          return response;
        }
        this.hasGameConfig = true;
        // 语言包在 ext_params.textList 中
        this.languagePack = extParams.textList || {};
        this.imagePack = extParams.picList || {};
        // 游戏真实名称，用于头部等展示
        this.realName = response.data.real_name || "";
        // 获取 show_point 配置，默认为 false
        this.showPoint = response.data.show_point;
        // 获取 show_share 配置，默认为 false
        this.showShare = response.data.show_share;
        // 获取 show_search 配置，默认为 false
        this.showSearch = response.data.show_search;

        // 在 languagePack 更新后，立即设置引用供 i18n 使用
        setLanguageStore(this);

        // 在 imagePack 更新后，设置 title 和 favicon
        this.setTitleAndFavicon();
      }
      return response;
    },

    /**
     * 设置当前语言
     * @param language - 语言代码（可能是 "all" 或具体语言代码）
     */
    setLanguage(language: string) {
      this.curLanguage = language;
      // 保存到 localStorage
      saveLanguageToStorage(GAME_ID, language);
    },

    /**
     * 翻译评论内容
     * @param pid - 评论ID
     * @param message - 评论内容（html的内容）
     * @param target_language - 目标语种
     */
    async translate(pid: number, message: string, target_language: string) {
      const response = await translationApi.translate(
        pid,
        message,
        GAME_ID,
        target_language
      );
      if (response.code == 200 && response.data?.message) {
        // 保存翻译后的内容
        this.translatedContents[pid] = response.data.message;
      }
      return response;
    },

    /**
     * 获取翻译后的内容
     * @param pid - 评论ID
     * @returns 翻译后的内容，如果未翻译则返回 undefined
     */
    getTranslatedContent(pid: number): string | undefined {
      return this.translatedContents[pid];
    },

    /**
     * 检查是否已翻译
     * @param pid - 评论ID
     * @returns 是否已翻译
     */
    isTranslated(pid: number): boolean {
      return !!this.translatedContents[pid];
    },

    /**
     * 获取当前语种的素材图片
     * @param key - 素材图片key
     * @returns 素材图片
     */
    getImage(key: string): string {
      if (!this.imagePack || Object.keys(this.imagePack).length === 0) {
        return "";
      }
      return (
        this.imagePack[this.curLanguage]?.[key] ||
        this.imagePack["default"]?.[key] ||
        ""
      );
    },

    /**
     * 根据语言代码获取语言名称（保留此方法用于兼容性，推荐使用 currentLanguageName getter）
     * @param languageCode - 语言代码
     * @returns 语言名称
     */
    getLanguageName(languageCode: string): string {
      if (!languageCode) return "";
      const lang = this.languageList.find((l: any) => l.code === languageCode);
      return lang?.name || languageCode;
    },

    /**
     * 检查语言是否在语言列表中
     * @param languageCode - 语言代码
     * @returns 是否在列表中
     */
    isLanguageInList(languageCode: string): boolean {
      return this.languageList.some((lang: any) => lang.code === languageCode);
    },

    /**
     * 设置页面标题和 favicon
     * 优先从 imagePack 获取，如果获取不到则使用 gameConfig 中的配置
     */
    setTitleAndFavicon() {
      const config = getGameConfig();

      // 设置 title
      if (config.title) {
        document.title = config.title;
      }

      // 设置 favicon
      const iconLink = document.getElementById("iconLink");
      if (iconLink) {
        // 先尝试从 language store 获取 favicon
        const faviconFromStore = this.getImage("favicon");
        const favicon = faviconFromStore || config.favicon;
        if (favicon) {
          (iconLink as HTMLLinkElement).href = favicon;
        }
      }
    },

    /**
     * 初始化语言设置
     * 优先级：localStorage > 默认"全部"（当语言列表长度 > 1 时）> API 默认语言
     * @param gameId - 游戏ID
     */
    async initializeLanguage(gameId: string) {
      // 1. 先获取语言列表和默认语言
      await this.getLanguageList(gameId);
      await this.getExtParams(gameId);
      // 2. 确定用户选择的语言（可能是 "all" 或具体语言代码）
      // 注意："all" 只是用于显示和筛选，不代表实际的 i18n locale
      let selectedLanguage: string = this.defaultLanguage;

      // 3. 从 localStorage 读取保存的语言（根据 gameId 区分）
      const savedLanguage = getLanguageFromStorage(gameId);
      if (savedLanguage && this.isLanguageInList(savedLanguage)) {
        selectedLanguage = savedLanguage;
      } else if (this.languageList.length > 1) {
        // 4. 如果没有保存的语言，且语言列表长度大于1，默认选中"全部"
        selectedLanguage = "all";
      }

      // 5. 设置 i18n 的 locale
      // 重要：如果选择的是 "all"，则使用 defaultLanguage 作为 locale
      // 因为 "all" 只是筛选选项，实际的界面语言仍使用默认语言
      const localeToSet =
        selectedLanguage === "all" ? this.defaultLanguage : selectedLanguage;
      setI18nLocale(localeToSet);

      // 6. 设置当前选择的语言（用于显示和筛选，可能是 "all"）
      this.setLanguage(selectedLanguage);

      return selectedLanguage;
    },
  },
});
